import React, { Children } from 'react';
import { motion } from 'framer-motion';
interface ProblemCardData {
  title: string;
  description: string;
  icon: React.ReactNode;
}
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    x: -40
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
function WarningIcon() {
  return <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true" data-id="element-221">
      <path d="M14 3L26 25H2L14 3Z" stroke="#FF6B6B" strokeWidth="2" fill="none" data-id="element-222" />
      <line x1="14" y1="11" x2="14" y2="18" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round" data-id="element-223" />
      <circle cx="14" cy="21.5" r="1.2" fill="#FF6B6B" data-id="element-224" />
    </svg>;
}
function EyeOffIcon() {
  return <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true" data-id="element-225">
      <path d="M4 14s3.5-7 10-7 10 7 10 7-3.5 7-10 7S4 14 4 14z" stroke="#FF6B6B" strokeWidth="2" fill="none" data-id="element-226" />
      <circle cx="14" cy="14" r="3" stroke="#FF6B6B" strokeWidth="2" fill="none" data-id="element-227" />
      <line x1="5" y1="5" x2="23" y2="23" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round" data-id="element-228" />
    </svg>;
}
function CogIcon() {
  return <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true" data-id="element-229">
      <circle cx="14" cy="14" r="4" stroke="#FF6B6B" strokeWidth="2" fill="none" data-id="element-230" />
      <path d="M14 2v3M14 23v3M2 14h3M23 14h3M5.1 5.1l2.1 2.1M20.8 20.8l2.1 2.1M5.1 22.9l2.1-2.1M20.8 7.2l2.1-2.1" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round" data-id="element-231" />
    </svg>;
}
function GhostIcon() {
  return <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true" data-id="element-232">
      <path d="M7 26V14a7 7 0 0114 0v12l-3.5-2.5L14 26l-3.5-2.5L7 26z" stroke="#FF6B6B" strokeWidth="2" fill="none" data-id="element-233" />
      <circle cx="11" cy="13" r="1.5" fill="#FF6B6B" data-id="element-234" />
      <circle cx="17" cy="13" r="1.5" fill="#FF6B6B" data-id="element-235" />
    </svg>;
}
function ProblemCard({
  title,
  description,
  icon
}: ProblemCardData) {
  return <motion.div variants={cardVariants} className="bg-navy-light/60 backdrop-blur-sm border-l-4 border-coral rounded-r-xl p-6 md:p-8" data-id="element-236">
      <div className="flex items-start gap-4" data-id="element-237">
        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-coral/10 flex items-center justify-center" data-id="element-238">
          {icon}
        </div>
        <div data-id="element-239">
          <h3 className="font-display font-bold text-lg text-white mb-2" data-id="element-240">
            {title}
          </h3>
          <p className="font-body text-slate-400 leading-relaxed text-sm md:text-base" data-id="element-241">
            {description}
          </p>
        </div>
      </div>
    </motion.div>;
}
export function Problems() {
  return <section id="problems" className="w-full bg-navy py-20 md:py-28 px-6" data-id="element-242">
      <div className="max-w-4xl mx-auto" data-id="element-243">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-14" data-id="element-244">
          <h2 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-white mb-4" data-id="element-245">
            The Retention Crisis in iGaming
          </h2>
          <p className="font-body text-slate-400 text-base md:text-lg max-w-2xl mx-auto" data-id="element-246">
            Operators are bleeding players — and outdated loyalty programs are
            the wound.
          </p>
        </motion.div>

        <motion.div className="space-y-5" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} data-id="element-247">
          <ProblemCard title="Generic Rewards Fatigue" description="Players receive the same bonuses as everyone else. No differentiation, no emotional connection, no reason to stay." icon={<WarningIcon data-id="element-249" />} data-id="element-248" />
          <ProblemCard title="Blind Personalization" description="Without behavioral intelligence, reward timing and selection are guesswork. Players feel unseen." icon={<EyeOffIcon data-id="element-251" />} data-id="element-250" />
          <ProblemCard title="Manual VIP Treadmill" description="VIP managers drown in spreadsheets, reacting to churn instead of preventing it. Scaling is impossible." icon={<CogIcon data-id="element-253" />} data-id="element-252" />
          <ProblemCard title="The Silent Churn Spiral" description="Disengaged players don't complain — they just leave. By the time you notice, they're playing elsewhere." icon={<GhostIcon data-id="element-255" />} data-id="element-254" />
        </motion.div>
      </div>
    </section>;
}